import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  insureds,
  type InsuredDetail,
  type InsuredPartner,
  type InsuredPartnerInput,
  type InsuredPartnerRole,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatRate, parseRate, rateToInput } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import {
  PENDENCIA_PARTICIPACAO,
  PENDENCIA_PEP_SOCIO,
  PENDENCIA_QUADRO,
  PERMISSAO_EDITAR,
  papeisDoQuadro,
  rotuloDaOrigem,
} from './rotulos';

/** Os tres blocos da aba, na ordem dela, com o texto do bloco vazio. */
const blocos: { papel: InsuredPartnerRole; titulo: string; vazio: string }[] = [
  { papel: 'socio', titulo: 'Socios', vazio: 'Nenhum socio cadastrado' },
  {
    papel: 'administrador',
    titulo: 'Administradores',
    vazio: 'Nenhum administrador cadastrado',
  },
  {
    papel: 'representante',
    titulo: 'Representantes',
    vazio: 'Nenhum representante cadastrado',
  },
];

/** Recuo da linha pelo nivel na arvore, para o quadro ler como o OnPoint. */
function recuoDoNivel(nivel: number): string {
  if (nivel <= 0) return '';
  if (nivel === 1) return 'pl-4';
  if (nivel === 2) return 'pl-8';
  return 'pl-12';
}

/**
 * Participacao digitada em percentual, em pontos-base, ou o motivo da recusa.
 *
 * Participacao com mais de duas casas nao cabe em ponto-base inteiro e e recusada,
 * sem arredondar, ate a decisao sobre a representacao exata (TEC-N02).
 */
function participacaoDigitada(texto: string): { bps: number | null; erro?: string } {
  if (texto.trim() === '') return { bps: null };
  const bps = parseRate(texto);
  if (bps === null) return { bps: null, erro: PENDENCIA_PARTICIPACAO };
  if (bps < 0 || bps > 10000) {
    return { bps: null, erro: 'A participacao precisa estar entre 0 e 100 por cento.' };
  }
  return { bps };
}

/**
 * Aba "Socios / Administradores" do segurado (P-SEG-2): os blocos Socios,
 * Administradores e Representantes, com a arvore do quadro (socio de socio pessoa
 * juridica), a participacao, o PEP e a origem da linha.
 *
 * O servidor confere empresa, permissao, documento e a arvore; a tela so mostra e
 * explica.
 */
export function InsuredPartnersTab({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can(PERMISSAO_EDITAR);
  const [editando, setEditando] = useState<{
    papel: InsuredPartnerRole;
    socio?: InsuredPartner;
  } | null>(null);
  const [removendo, setRemovendo] = useState<InsuredPartner | null>(null);

  const consulta = useQuery({
    queryKey: ['insured', segurado.id, 'partners'],
    queryFn: ({ signal }) => insureds.partners(segurado.id, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['insured', segurado.id] });
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'insured', segurado.id],
    });
  }

  const remover = useMutation({
    mutationFn: (socioId: string) => insureds.removePartner(segurado.id, socioId),
    onSuccess: () => {
      toast.success('Pessoa excluida do quadro.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const quadro = consulta.data ?? [];
  const abaixoDe = (socio: InsuredPartner) =>
    quadro.filter((outro) => outro.parent_id === socio.id).length;

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="info" title="Quadro societario">
        {PENDENCIA_QUADRO} {PENDENCIA_PEP_SOCIO}
      </Alert>

      {blocos.map((bloco) => {
        const linhas = quadro.filter((socio) => socio.role === bloco.papel);
        const colunas: Column<InsuredPartner>[] = [
          {
            key: 'nome',
            header: 'Nome',
            render: (socio) => (
              <div className={`min-w-0 ${recuoDoNivel(socio.level)}`}>
                <p className="truncate font-medium text-ink-900">{socio.name}</p>
                {socio.parent_id ? (
                  <p className="truncate text-2xs text-ink-500">
                    Socio de {socio.parent_label || 'outra pessoa do quadro'}
                  </p>
                ) : null}
                {socio.role_description ? (
                  <p className="truncate text-2xs text-ink-500">
                    {socio.role_description}
                  </p>
                ) : null}
              </div>
            ),
          },
          {
            key: 'documento',
            header: 'CPF/CNPJ',
            render: (socio) => (
              <div className="flex flex-col">
                <span className="whitespace-nowrap tabular-nums">
                  {formatCpfCnpj(socio.document)}
                </span>
                {socio.document_unchecked ? (
                  <span className="text-2xs text-warning-700">nao conferido</span>
                ) : null}
              </div>
            ),
          },
          {
            key: 'tipo',
            header: 'Tipo',
            hideBelow: 'sm',
            render: (socio) => (socio.is_company ? 'Pessoa juridica' : 'Pessoa fisica'),
          },
          ...(bloco.papel === 'socio'
            ? [
                {
                  key: 'participacao',
                  header: 'Participacao',
                  align: 'right' as const,
                  render: (socio: InsuredPartner) =>
                    socio.participation_bps === null ? (
                      <span className="text-ink-400">-</span>
                    ) : (
                      formatRate(socio.participation_bps)
                    ),
                },
                {
                  key: 'majoritario',
                  header: 'Majoritario',
                  render: (socio: InsuredPartner) =>
                    socio.is_majority ? 'Sim' : 'Nao',
                },
              ]
            : []),
          {
            key: 'pep',
            header: 'PEP',
            render: (socio) =>
              socio.is_pep ? (
                <Badge tone="warning">Sim</Badge>
              ) : (
                <Badge tone="neutral">Nao</Badge>
              ),
          },
          {
            key: 'origem',
            header: 'Origem',
            hideBelow: 'sm',
            render: (socio) => rotuloDaOrigem(socio.source),
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (socio: InsuredPartner) => (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditando({ papel: socio.role, socio })}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemovendo(socio)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ];
        return (
          <DetailCard
            key={bloco.papel}
            title={bloco.titulo}
            loading={consulta.isPending}
            error={consulta.error}
            action={
              podeEditar ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditando({ papel: bloco.papel })}
                >
                  Adicionar
                </Button>
              ) : undefined
            }
          >
            <DataTable
              columns={colunas}
              items={linhas}
              keyOf={(socio) => socio.id}
              empty={<EmptyState title={bloco.vazio} />}
            />
          </DetailCard>
        );
      })}

      {editando ? (
        <InsuredPartnerDialog
          seguradoId={segurado.id}
          papel={editando.papel}
          socio={editando.socio}
          quadro={quadro}
          onClose={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            invalidar();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => removendo && remover.mutate(removendo.id)}
        title="Excluir do quadro societario?"
        message={
          removendo && abaixoDe(removendo) > 0
            ? `${removendo.name} sai do quadro com as pessoas penduradas nela. A exclusao fica no historico do segurado.`
            : `${removendo?.name ?? ''} sai do quadro. A exclusao fica no historico do segurado.`
        }
        confirmLabel="Excluir"
        tone="danger"
        loading={remover.isPending}
      />
    </div>
  );
}

function InsuredPartnerDialog({
  seguradoId,
  papel,
  socio,
  quadro,
  onClose,
  onSalvo,
}: {
  seguradoId: string;
  papel: InsuredPartnerRole;
  socio: InsuredPartner | undefined;
  quadro: InsuredPartner[];
  onClose: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const [role, setRole] = useState<InsuredPartnerRole>(socio?.role ?? papel);
  const [nome, setNome] = useState(socio?.name ?? '');
  const [descricao, setDescricao] = useState(socio?.role_description ?? '');
  const [documento, setDocumento] = useState(socio?.document ?? '');
  const [participacao, setParticipacao] = useState(
    socio?.participation_bps != null ? rateToInput(socio.participation_bps) : '',
  );
  const [pep, setPep] = useState(socio?.is_pep ?? false);
  const [majoritario, setMajoritario] = useState(socio?.is_majority ?? false);
  const [pai, setPai] = useState(socio?.parent_id ?? '');

  const lida = participacaoDigitada(participacao);

  const salvar = useMutation({
    mutationFn: (entrada: InsuredPartnerInput) =>
      socio
        ? insureds.updatePartner(seguradoId, socio.id, entrada)
        : insureds.addPartner(seguradoId, entrada),
    onSuccess: () => {
      toast.success(socio ? 'Pessoa alterada.' : 'Pessoa incluida no quadro.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  // So uma pessoa juridica do quadro tem socios, e ninguem pertence a si mesmo.
  const pais = quadro
    .filter((outro) => outro.is_company && outro.id !== socio?.id)
    .map((outro) => ({ value: outro.id, label: outro.name }));

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (lida.erro) return;
    salvar.mutate({
      role,
      name: nome,
      role_description: descricao,
      document: documento,
      participation_bps: lida.bps,
      is_pep: pep,
      is_majority: majoritario,
      parent_id: pai || null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={socio ? `Alterar ${socio.name}` : 'Incluir no quadro societario'}
      description={PENDENCIA_PEP_SOCIO}
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Papel no quadro"
            options={papeisDoQuadro}
            value={role}
            error={fieldErrorOf(erro, 'role')}
            onChange={(evento) => setRole(evento.target.value as InsuredPartnerRole)}
          />
          <Field
            label="Nome da pessoa"
            required
            value={nome}
            error={fieldErrorOf(erro, 'name')}
            onChange={(evento) => setNome(evento.target.value)}
          />
          <Field
            label="CPF/CNPJ da pessoa"
            required
            value={documento}
            error={fieldErrorOf(erro, 'document')}
            onChange={(evento) => setDocumento(evento.target.value)}
          />
          <Field
            label="Descricao do papel"
            value={descricao}
            error={fieldErrorOf(erro, 'role_description')}
            onChange={(evento) => setDescricao(evento.target.value)}
          />
          <Field
            label="Participacao (%)"
            inputMode="decimal"
            value={participacao}
            error={lida.erro ?? fieldErrorOf(erro, 'participation_bps')}
            onChange={(evento) => setParticipacao(evento.target.value)}
          />
          {pais.length > 0 ? (
            <Select
              label="Socio de"
              placeholder="Direto no segurado"
              options={pais}
              value={pai}
              error={fieldErrorOf(erro, 'parent_id')}
              onChange={(evento) => setPai(evento.target.value)}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-ink-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pep}
              onChange={(evento) => setPep(evento.target.checked)}
            />
            Pessoa politicamente exposta (PEP)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={majoritario}
              onChange={(evento) => setMajoritario(evento.target.checked)}
            />
            Socio majoritario
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending} disabled={!!lida.erro}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
